<template>
  <v-data-table
    :headers="headers"
    :items="notifications"
    :items-per-page="10"
    class="transparent ui-module-fade-in-up"
    height="560px"
    :footer-props="{
      'items-per-page-options': [5, 10, 15, 20, -1],
      'items-per-page-text': 'Registros por página:',
    }"
    :item-class="getRowClass"
    calculate-widths
  >
    <template #[`item.prioridad`]="{ item }">
      <NotificationPriorityChip :priority="item.prioridad" />
    </template>
    <template #[`item.categoria`]="{ item }">
      <NotificationCategoryChip :category="item.categoria" />
    </template>
    <template #[`item.mensaje`]="{ item }">
      <section class="py-2">
        <h4>{{ item.titulo }}</h4>
        <div>{{ item.mensaje }}</div>
      </section>
    </template>
    <template v-slot:[`item.opciones`]="{ item }">
      <div class="d-flex justify-center" v-if="shouldShowMarkAsReadButton(item)">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <IconButton small v-bind="attrs" v-on="on" variant="secondary" icon="mdi-check" @click="markAsRead(item)" />
          </template>
          <span>Marcar como leído</span>
        </v-tooltip>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import NotificationPriorityChip from './NotificationPriorityChip.vue'
import NotificationCategoryChip from './NotificationCategoryChip.vue'
import IconButton from '../base/IconButton.vue'
import { mapActions, mapGetters } from 'vuex'
import AREAS_PERFILES from '@/modules/notificaciones/domain/AreasPerfiles.js'
import AREAS from '@/modules/notificaciones/domain/areas'

const AREA_NOTIFICACION_KEYS = {
  [AREAS.RECURSOS_HUMANOS]: 'recursos_humanos',
  [AREAS.ARMERIA]: 'armeria',
  [AREAS.EQUIPAMIENTO]: 'equipamiento',
  [AREAS.IFCSP]: 'ifcsp',
  [AREAS.DESARROLLO_POLICIAL]: 'desarrollo_policial',
  [AREAS.ENLACE_C3]: 'enlace_c3',
  [AREAS.REGISTRO_PERSONAL]: 'registro_personal',
  [AREAS.ENLACE_COMISARIA]: 'enlace_comisarias',
  [AREAS.EJECUTIVO]: 'ejecutivo',
}

export default {
  components: {
    NotificationPriorityChip,
    NotificationCategoryChip,
    IconButton,
  },

  props: {
    notifications: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      AREAS_PERFILES,
      AREA_NOTIFICACION_KEYS,
    }
  },

  computed: {
    ...mapGetters({ perfil: 'getPerfil' }),

    headers() {
      return [
        { text: 'Fecha', value: 'fecha' },
        { text: 'Prioridad', value: 'prioridad' },
        { text: 'Categoría', value: 'categoria' },
        { text: 'Mensaje', value: 'mensaje' },
        ...(this.perfil !== 'ADMINSITRADOR GENERAL'
          ? [{ text: 'Opciones', value: 'opciones', align: 'center', sortable: false }]
          : []),
      ]
    },
  },

  methods: {
    ...mapActions('notificaciones', ['updateNotification']),
    markAsRead(notification) {
      this.updateNotification({ idNotificacion: notification.id, notificacion: notification }).then(() => {
        const area = this.getAreaKeyByPerfil(this.perfil)
        const keyProp = this.AREA_NOTIFICACION_KEYS[area]

        if (keyProp) {
          notification[keyProp] = 2
        }
      })
    },

    GetAreaFromTitle(title) {
      const area = title.slice(0, title.indexOf(':')).trim()

      return area
    },

    getAreaKeyByPerfil(perfil) {
      if (!perfil) return null
      for (const [areaKey, perfiles] of Object.entries(AREAS_PERFILES)) {
        if (Array.isArray(perfiles) && perfiles.includes(perfil)) {
          return areaKey
        }
      }
      return null
    },

    shouldShowMarkAsReadButton(notification) {
      const area = this.getAreaKeyByPerfil(this.perfil)
      const keyProp = AREA_NOTIFICACION_KEYS[area]

      return notification[keyProp] !== 2
    },

    getRowClass(notification) {
      const area = this.getAreaKeyByPerfil(this.perfil)
      const keyProp = this.AREA_NOTIFICACION_KEYS[area]

      return notification[keyProp] === 2 ? 'row-read' : ''
    },
  },
}
</script>

<style scoped>
::v-deep .v-data-table {
  background: transparent !important;
  color: #e0e0e0 !important;
  border-radius: 12px;
}

/* Encabezado */
::v-deep .v-data-table-header tr {
  height: 48px !important;
}

::v-deep .v-data-table-header {
  position: relative;
}

::v-deep .v-data-table-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--v-secondary-base);
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
}

::v-deep .v-data-table-header th {
  color: #e0e0e0 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid var(--v-secondary-base) !important;
  padding: 0 16px !important;
  height: 48px !important;
  vertical-align: middle !important;
  position: relative;
  z-index: 1;
}

/* El span interno de Vuetify que contiene el texto del header */
::v-deep .v-data-table-header th > span {
  display: inline-flex;
  align-items: center;
  height: 100%;
}

/* Iconos de ordenamiento en header */
::v-deep .v-data-table-header th .v-icon {
  color: rgba(var(--v-secondary-base-rgb), 0.7) !important;
  vertical-align: middle !important;
}

/* Filas */
::v-deep .v-data-table tbody tr {
  transition: all 0.2s ease;
}

::v-deep .v-data-table tbody tr:hover {
  background: rgba(var(--v-secondary-base-rgb), 0.12) !important;
}

::v-deep .v-data-table tbody td {
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(var(--v-secondary-base-rgb), 0.08) !important;
  padding: 14px 16px !important;
  font-size: 0.9375rem;
  vertical-align: middle !important;
}

/* Fila leída */
::v-deep .v-data-table .row-read {
  background-color: rgba(190, 181, 181, 0.15) !important;
}

/* Textos dentro de celdas */
::v-deep .v-data-table tbody h4 {
  color: #ffffff !important;
  font-weight: 600;
  margin-bottom: 2px;
}

/* Empty state */
::v-deep .v-data-table__empty-wrapper {
  color: rgba(224, 224, 224, 0.6) !important;
}

/* Footer / paginación */
::v-deep .v-data-footer {
  border-top: 1px solid rgba(var(--v-secondary-base-rgb), 0.15) !important;
  color: rgba(224, 224, 224, 0.7) !important;
}

::v-deep .v-data-footer .v-select__selection,
::v-deep .v-data-footer .v-icon {
  color: rgba(224, 224, 224, 0.7) !important;
}

::v-deep .v-data-footer__pagination {
  color: rgba(224, 224, 224, 0.7) !important;
}

/* Botones */
::v-deep .v-btn {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  position: relative;
  overflow: hidden;
}

::v-deep .v-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
}

::v-deep .v-btn:active::before {
  width: 300px;
  height: 300px;
}

::v-deep .v-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

::v-deep .v-btn .v-icon {
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

/* Tooltips */
::v-deep .v-tooltip__content {
  background: rgba(30, 30, 46, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid var(--v-secondary-base) !important;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  color: #e0e0e0 !important;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 8px 12px;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(10, 10, 10, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--v-secondary-base);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  filter: brightness(1.2);
}
</style>
